import { Link } from "react-router-dom";
import { Camera, Sparkles, Trophy, Wallet, MapPin, Shield } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/** Public landing — no data, no auth required. Pitch + sign-in CTA. */
export default function TrashLanding() {
  const { user } = useAuth();
  return (
    <div className="space-y-6 py-4" data-testid="trash-landing">
      <section className="rounded-3xl border-2 border-[#39FF14]/40 bg-gradient-to-br from-[#39FF14]/20 via-[#00F0FF]/10 to-transparent p-6 text-center relative overflow-hidden">
        <div className="text-[10px] uppercase tracking-widest text-[#39FF14] font-bold mb-2">Real-world cleanup · Real rewards</div>
        <h1 className="font-black text-4xl sm:text-5xl leading-tight">Clean the world. <span className="text-[#39FF14]">Earn rewards.</span> Level up.</h1>
        <p className="text-white/70 mt-3 text-sm">Snap a photo of litter with your phone. Jixxeo A.I identifies it. Pick it up. Snap the after shot. Earn XP, Jixxeo Coins, and real redeemable cash.</p>
        {user ? (
          <Link to="/trashhunter" data-testid="landing-enter"
            className="inline-flex items-center gap-1.5 bg-[#39FF14] text-black font-black text-sm uppercase tracking-widest px-6 py-3 rounded-full mt-5">
            <Camera className="w-4 h-4" /> Start cleaning
          </Link>
        ) : (
          <div className="flex gap-2 justify-center mt-5">
            <Link to="/login?next=/trashhunter" data-testid="landing-signin"
              className="inline-flex items-center gap-1.5 bg-[#39FF14] text-black font-black text-sm uppercase tracking-widest px-6 py-3 rounded-full">
              Sign in
            </Link>
            <Link to="/register?next=/trashhunter" data-testid="landing-register"
              className="inline-flex items-center gap-1.5 border border-white/30 bg-white/5 font-black text-sm uppercase tracking-widest px-6 py-3 rounded-full">
              Sign up
            </Link>
          </div>
        )}
      </section>

      <div className="grid grid-cols-2 gap-3">
        <Feature icon={<Camera className="w-5 h-5" />} title="Camera-only" desc="Scan real litter live from your phone. No gallery photos accepted." />
        <Feature icon={<Sparkles className="w-5 h-5" />} title="A.I detection" desc="Jixxeo A.I identifies the trash type and confidence." />
        <Feature icon={<MapPin className="w-5 h-5" />} title="GPS verified" desc="Location + cooldown checks stop staged scans." />
        <Feature icon={<Trophy className="w-5 h-5" />} title="Missions & badges" desc="Daily and weekly missions, streaks, and level up." />
        <Feature icon={<Wallet className="w-5 h-5" />} title="Real cash payouts" desc="$5 minimum · $0.10 fee · unlock after 5 verified cleanups." />
        <Feature icon={<Shield className="w-5 h-5" />} title="Anti-cheat" desc="Duplicate-photo, geo-cooldown, and daily earning cap." />
      </div>

      <section className="rounded-2xl border border-white/10 bg-white/5 p-4 text-xs text-white/60 space-y-1">
        <div className="font-black text-white/80 uppercase tracking-widest text-[10px]">How it works</div>
        <div>1. Sign in and open Scan.</div>
        <div>2. Snap a photo of the litter → we identify it.</div>
        <div>3. Pick it up. Snap the after shot from the same spot.</div>
        <div>4. Earn XP · Coins · Cash. Cash out after 5 verified cleanups.</div>
      </section>
    </div>
  );
}

function Feature({ icon, title, desc }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/40 p-4">
      <div className="w-9 h-9 rounded-lg bg-[#39FF14]/15 text-[#39FF14] flex items-center justify-center mb-2">{icon}</div>
      <div className="font-black text-sm">{title}</div>
      <div className="text-xs text-white/60 mt-1">{desc}</div>
    </div>
  );
}
